/**
 * 림월드식 시트: 층 아틀라스(`public/sprites/rim.png`)를 받아, 렌더러가 부르는 차림마다 프레임을 구워(`rim-bake.ts`)
 * `UnitSheet` 모양으로 넘긴다. 렌더러는 `keyFor(spec)`(차림 열쇠 `lookKey`)로 `frames`를 부른다.
 *
 * 개발 중에는 주소의 `?rim=1`로 켜고 옛 시트(`units.png`)를 기본으로 두었다. 층 그림이 다 모여 이것만 남겼다.
 * 렌더링 전용이다.
 */
import { CanvasSource, Rectangle, Texture } from 'pixi.js';
import { RIM_MANIFEST } from './rim-manifest.ts';
import {
  RIM_ATTACK_FRAMES,
  RIM_ATTACK_HIT,
  RIM_ATTACK_MS,
  RIM_TOP,
  bakeLook,
  lookKey,
  lookHands,
  parseLook,
} from './rim-bake.ts';
import { foeLeaderLook, lookFor, resolve } from './looks.ts';
import { BranchCode } from '../sim/troops.ts';
import type { UnitFrames, UnitSheet } from './unit-sheet.ts';

export function rimAtlasUrl(base: string = import.meta.env.BASE_URL): string {
  return `${base}sprites/rim.png`;
}

let atlasPromise: Promise<HTMLImageElement | null> | null = null;

/** 층 아틀라스 그림. 화면(병영·편성의 작은 그림)도 같은 그림을 쓴다. 못 받으면 null. */
export function loadAtlas(base: string = import.meta.env.BASE_URL): Promise<HTMLImageElement | null> {
  const attempt = (atlasPromise ??= new Promise<HTMLImageElement | null>((res) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => res(null);
    img.src = rimAtlasUrl(base);
  }));
  // 실패는 붙들지 않는다 — 다음 출정에 다시 받는다.
  void attempt.then((got) => {
    if (!got && atlasPromise === attempt) atlasPromise = null;
  });
  return attempt;
}

/**
 * 림월드식 `UnitSheet`. 아틀라스를 못 받거나 GPU 한계보다 크면 null — 렌더러는 예전 코드 그림(`atlas.ts`)으로 그린다.
 * `recolor`: 적군본 만들기(붉은 천 → 초록, `unit-sheet.ts`의 `enemyRecolor`).
 */
export async function loadRimSheet(
  maxSize: number,
  recolor: (c: HTMLCanvasElement) => HTMLCanvasElement,
  base: string = import.meta.env.BASE_URL,
): Promise<UnitSheet | null> {
  const img = await loadAtlas(base);
  if (!img) return null;
  const man = RIM_MANIFEST;
  const n = man.cell;
  if (n * 33 > maxSize) return null;
  const h = n + RIM_TOP;
  // 굽는 것은 차림마다 한 번(그림이 없는 층을 떨군 열쇠로 묶는다 — 같은 모습이면 같은 텍스처).
  const baked = new Map<string, UnitFrames>();
  return {
    cell: n,
    footAnchor: (man.feet + RIM_TOP + 1) / h,
    attackMs: RIM_ATTACK_MS,
    attackHit: RIM_ATTACK_HIT,
    keyFor: (spec) => lookKey(lookFor(spec)),
    keyForFoeLeader: (leaderId, spec) => {
      const look = foeLeaderLook(leaderId, spec.culture, spec.branch === BranchCode.Horse && spec.dismounted !== true, man);
      return look ? lookKey(look) : null;
    },
    frames(key: string, isEnemy: boolean): UnitFrames | null {
      const look = resolve(parseLook(key), man);
      const ck = `${lookKey(look)}|${isEnemy ? 1 : 0}`;
      let fr = baked.get(ck);
      if (fr) return fr;
      let canvas = bakeLook(img, man, look);
      if (isEnemy) canvas = recolor(canvas);
      const source = new CanvasSource({ resource: canvas, scaleMode: 'nearest' });
      const at = (slot: number): Texture => new Texture({ source, frame: new Rectangle(slot * n, 0, n, h) });
      fr = {
        stand: [0, 1, 2, 3].map((d) => at(d)),
        walkA: [0, 1, 2, 3].map((d) => at(4 + d)),
        walkB: [0, 1, 2, 3].map((d) => at(8 + d)),
        hit: at(12),
        hit2: at(13),
        death: [at(14), at(15), at(16)],
        // 손 자리는 말(기수가 앉는 높이)마다 다르다 — 차림마다 구울 때 함께 잰다.
        hands: lookHands(man, look.horse),
        attack: [0, 1, 2, 3].map((d) => Array.from({ length: RIM_ATTACK_FRAMES }, (_, f) => at(17 + d * RIM_ATTACK_FRAMES + f))),
      };
      baked.set(ck, fr);
      return fr;
    },
  };
}
